import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Box, Button, Typography } from '@mui/material';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import Contact from '@/components/application_components/question_types/Contact';
import CommentsWrap from '@/components/comments_components/CommentsWrap';
import { CurrentApplicationContext } from '@/Contexts/CurrentApplicationContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import useContact from '@/hooks/useContact';
import { ContactProps, localizedTextEquals } from '@/models/Contact';
import { QuestionProps } from '@/models/Question';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import { Locales } from '@/models/User';

// Keep as a constant so we don't recreate it every render.
const BLANK_CONTACT: ContactProps = {
  id: null,
  firstName: null,
  lastName: null,
  phone: null,
  email: null,
  streetAddress1: null,
  streetAddress2: null,
  city: null,
  countyId: null,
  stateId: null,
  countryId: null,
  zipCode: null,
  consent: null,
  certification: null,
  departmentOther: null,
  contactSubTypeId: null,
  title: null,
  linkedIn: null,
  whatsApp: null,
  yearsExperience: null,
  departmentTypeId: null,
  contactRelationships: [],
};

type ContactQuestionProps = {
  contact: ContactProps | null;
  showLoader: boolean;
};

function getContactMappings(
  contactTypeId: number,
  currentContacts: ContactProps[] | null,
  applicationId: number
) {
  const relevant = (currentContacts ?? []).filter((contact) =>
    hasRelationshipForType(contact, applicationId, contactTypeId)
  );

  // If none exist, show a single blank contact to start.
  if (relevant.length === 0) {
    return [
      {
        contact: BLANK_CONTACT,
        showLoader: false,
      },
    ];
  }

  // Map relationships to actual contacts (if found)
  const mappings: ContactQuestionProps[] = relevant.map((rel) => ({
    contact: rel,
    showLoader: false,
  }));

  return mappings;
}

function hasRelationshipForType(
  contact: ContactProps,
  applicationId: number,
  contactTypeId: number
): boolean {
  return (contact.contactRelationships ?? []).some(
    (relationship) =>
      relationship.applicationId === applicationId && relationship.contactTypeId === contactTypeId
  );
}

function mergePreviewContactsForType(
  existingContacts: ContactProps[] | null,
  updatedContacts: ContactProps[],
  applicationId: number,
  contactTypeId: number
): ContactProps[] {
  const ensureRelationship = (contact: ContactProps): ContactProps => {
    if (hasRelationshipForType(contact, applicationId, contactTypeId)) {
      return contact;
    }

    return {
      ...contact,
      contactRelationships: [
        ...(contact.contactRelationships ?? []),
        { applicationId, contactTypeId },
      ],
    };
  };

  const strippedExisting = (existingContacts ?? [])
    .map((contact) => ({
      ...contact,
      contactRelationships: (contact.contactRelationships ?? []).filter(
        (relationship) =>
          !(
            relationship.applicationId === applicationId &&
            relationship.contactTypeId === contactTypeId
          )
      ),
    }))
    .filter((contact) => (contact.contactRelationships ?? []).length > 0);

  const mergedById = new Map<number, ContactProps>();
  const result: ContactProps[] = [];

  for (const contact of strippedExisting) {
    if (contact.id != null) {
      mergedById.set(contact.id, contact);
    } else {
      result.push(contact);
    }
  }

  for (const contact of updatedContacts.map(ensureRelationship)) {
    if (contact.id != null) {
      const current = mergedById.get(contact.id);
      mergedById.set(contact.id, {
        ...(current ?? {}),
        ...contact,
        contactRelationships: [
          ...(current?.contactRelationships ?? []),
          ...(contact.contactRelationships ?? []),
        ],
      });
    } else {
      result.push(contact);
    }
  }

  return [...result, ...Array.from(mergedById.values())];
}

export default function ContactQuestion({ question }: { question: QuestionProps }) {
  const {
    postContactRelationship,
    updateContactRelationship,
    deleteContactRelationship,
    isContactValid,
  } = useContact();
  const { setAlert } = useContext(SnackbarContext);
  const {
    currentContacts,
    isPreviewMode,
    updateContacts,
    updateApplicationContactRelationships,
    currentApplication,
    answers,
    canEdit,
    saveApplicationAnswer,
  } = useContext(CurrentApplicationContext);
  const application = currentApplication;
  const contactConfig = question.contactConfig!;

  const { locale, translatedText } = useContext(LocaleContext);

  const [selectedContactMappings, setSelectedContactMappings] = useState<ContactQuestionProps[]>(
    []
  );

  const questionAnswer = answers[question.id];

  // -------------------------
  // Validation -> auto answer
  // -------------------------
  const allContactsValid = useMemo((): boolean | null => {
    if (selectedContactMappings == null || selectedContactMappings.length === 0) return null;

    return selectedContactMappings.every((m) => {
      if (m.contact == null) return false;
      return isContactValid(m.contact, contactConfig, application.programId);
    });
  }, [contactConfig, application.programId, isContactValid, selectedContactMappings]);

  // Tracks the completion value currently being saved so a re-run of this effect
  // (e.g. contacts settling one at a time) can't fire a second, overlapping save
  // for the same target before the first one's response updates questionAnswer -
  // that race was producing duplicate-key inserts on QuestionResponse.
  const pendingCompletionSaveRef = useRef<string | null>(null);

  useEffect(() => {
    const saveAnswer = async (
      applicationId: number,
      questionId: number,
      answerString: string | null,
      selectedLocale: Locales
    ) => {
      try {
        await saveApplicationAnswer(
          applicationId,
          questionId,
          answerString,
          'string',
          selectedLocale
        );
      } finally {
        if (pendingCompletionSaveRef.current === (answerString ?? 'null')) {
          pendingCompletionSaveRef.current = null;
        }
      }
    };

    if (allContactsValid == null) return;
    if (questionAnswer == null) return;
    if (locale == null) return;

    const isCompleted = questionAnswer.responseString === 'completed';
    const target = allContactsValid ? 'completed' : null;

    if (target === (isCompleted ? 'completed' : null)) return;
    if (pendingCompletionSaveRef.current === (target ?? 'null')) return;

    pendingCompletionSaveRef.current = target ?? 'null';
    void saveAnswer(application.id, question.id, target, locale);
  }, [
    allContactsValid,
    application.id,
    locale,
    question.id,
    questionAnswer,
    saveApplicationAnswer,
  ]);

  // Sync selected contacts with latest application contacts.
  useEffect(() => {
    const mappings = getContactMappings(contactConfig.id, currentContacts, application.id);
    setSelectedContactMappings(mappings);
  }, [application.id, currentContacts, question.contactConfig, contactConfig.id]);

  const addNewContact = useCallback(() => {
    if (selectedContactMappings.length >= contactConfig.maxInstances) {
      setAlert({
        message: translatedText.contact.messageMaximum,
        severity: 'error',
      });
      return;
    }

    setSelectedContactMappings((prev) => [
      ...prev,
      {
        contact: BLANK_CONTACT,
        showLoader: false,
      },
    ]);
  }, [
    contactConfig.maxInstances,
    selectedContactMappings.length,
    setAlert,
    translatedText.contact.messageMaximum,
  ]);

  const getCurrentTypeContacts = useCallback((): ContactProps[] => {
    return (currentContacts ?? []).filter((contact) =>
      contact.contactRelationships.some(
        (relationship) =>
          relationship.applicationId === application.id &&
          relationship.contactTypeId === contactConfig.id
      )
    );
  }, [application.id, contactConfig.id, currentContacts]);

  const syncContactRelationshipById = useCallback(
    async (contactId: number) => {
      const didUpdate = await updateContactRelationship(
        contactId,
        application.id,
        contactConfig.id
      );
      if (!didUpdate) {
        return false;
      }
      await updateApplicationContactRelationships();
      return true;
    },
    [
      application.id,
      contactConfig.id,
      updateApplicationContactRelationships,
      updateContactRelationship,
    ]
  );

  const addContactRelationshipById = useCallback(
    async (contactId: number) => {
      const didAdd = await postContactRelationship(contactId, application.id, contactConfig.id);
      if (!didAdd) {
        return false;
      }
      await updateApplicationContactRelationships();
      return true;
    },
    [
      application.id,
      contactConfig.id,
      postContactRelationship,
      updateApplicationContactRelationships,
    ]
  );

  const deleteContactRelationshipById = useCallback(
    async (contactId: number) => {
      const didDelete = await deleteContactRelationship(
        contactId,
        application.id,
        contactConfig.id,
        isPreviewMode
      );
      if (!didDelete) {
        return false;
      }
      await updateApplicationContactRelationships();
      return true;
    },
    [
      application.id,
      contactConfig.id,
      deleteContactRelationship,
      isPreviewMode,
      updateApplicationContactRelationships,
    ]
  );

  const handleContactSave = useCallback(
    async (contact: ContactProps) => {
      try {
        if (isPreviewMode) {
          const currentTypeContacts = getCurrentTypeContacts();
          const nextTypeContacts =
            contactConfig.maxInstances === 1
              ? [contact]
              : (() => {
                  const existingIndex = currentTypeContacts.findIndex((c) => c.id === contact.id);
                  if (existingIndex >= 0) {
                    const updated = [...currentTypeContacts];
                    updated[existingIndex] = { ...updated[existingIndex], ...contact };
                    return updated;
                  }

                  return [...currentTypeContacts, contact];
                })();

          const mergedContacts = mergePreviewContactsForType(
            currentContacts,
            nextTypeContacts,
            application.id,
            contactConfig.id
          );
          updateContacts(mergedContacts);
        } else {
          if (contact.id == null) {
            return false;
          }

          const isExistingTypeContact = getCurrentTypeContacts().some((c) => c.id === contact.id);

          if (contactConfig.maxInstances === 1) {
            const didReplace = await syncContactRelationshipById(contact.id);
            if (!didReplace) {
              setAlert({
                message: translatedText.contact.messageContactSaveFailed,
                severity: 'error',
              });
              return false;
            }
          } else if (!isExistingTypeContact) {
            const didAdd = await addContactRelationshipById(contact.id);
            if (!didAdd) {
              setAlert({
                message: translatedText.contact.messageContactSaveFailed,
                severity: 'error',
              });
              return false;
            }
          }
        }

        setAlert({
          message: translatedText.contact.messageSaved,
          severity: 'success',
          autoHideDuration: 3000,
        });

        return true;
      } catch {
        setAlert({
          message: translatedText.contact.messageContactSaveFailed,
          severity: 'error',
        });

        return false;
      }
    },
    [
      application.id,
      contactConfig.id,
      isPreviewMode,
      setAlert,
      translatedText.contact.messageContactSaveFailed,
      translatedText.contact.messageSaved,
      currentContacts,
      contactConfig.maxInstances,
      getCurrentTypeContacts,
      updateContacts,
      addContactRelationshipById,
      syncContactRelationshipById,
    ]
  );

  const handleContactDelete = useCallback(
    async (contactId: number) => {
      try {
        if (isPreviewMode) {
          const currentTypeContacts = getCurrentTypeContacts();
          const nextTypeContacts = currentTypeContacts.filter(
            (contact) => contact.id !== contactId
          );
          if (currentTypeContacts.length === nextTypeContacts.length) {
            setAlert({
              message: translatedText.contact.messageContactSaveFailed,
              severity: 'error',
            });
            return false;
          }
          const mergedContacts = mergePreviewContactsForType(
            currentContacts,
            nextTypeContacts,
            application.id,
            contactConfig.id
          );
          updateContacts(mergedContacts);
        } else {
          const didDelete = await deleteContactRelationshipById(contactId);
          if (!didDelete) {
            setAlert({
              message: translatedText.contact.messageContactSaveFailed,
              severity: 'error',
            });
            return false;
          }
        }

        setAlert({
          message: translatedText.contact.messageContactRemoved,
          severity: 'success',
        });

        return true;
      } catch {
        setAlert({
          message: translatedText.contact.messageContactSaveFailed,
          severity: 'error',
        });
        return false;
      }
    },
    [
      contactConfig.id,
      application.id,
      isPreviewMode,
      translatedText.contact.messageContactRemoved,
      translatedText.contact.messageContactSaveFailed,
      currentContacts,
      deleteContactRelationshipById,
      getCurrentTypeContacts,
      updateContacts,
      setAlert,
    ]
  );

  const maxInstances = contactConfig.maxInstances;
  const hasUnsaved = selectedContactMappings.some((m) => m?.contact?.id == null);
  const canAddAnother = selectedContactMappings.length < maxInstances && !hasUnsaved;

  return (
    <>
      {localizedTextEquals(
        contactConfig.contactTypeName,
        'Community Tree Board Member',
        locale
      ) && (
        <Typography width='85%' variant='body2'>
          {translatedText.contact?.treeBoardLimitText?.(maxInstances)}
        </Typography>
      )}

      <Box display='flex' width='100%' justifyContent='space-between' alignItems='start'>
        <Box display='flex' width='100%' flexDirection='column'>
          {selectedContactMappings.map((m, index) => (
            <Contact
              key={`${question.id}-${m.contact?.id ?? 'new'}-${index}`}
              question={question}
              order={index}
              initialContact={m.contact ?? BLANK_CONTACT}
              onSave={handleContactSave}
              onDelete={handleContactDelete}
            />
          ))}

          {canAddAnother && (
            <Button
              disabled={!canEdit}
              sx={{ mt: 2, alignSelf: 'start' }}
              variant='secondary'
              size='small'
              startIcon={<AddCircleOutlineIcon />}
              onClick={addNewContact}
            >
              {translatedText.contact.buttonAdd}
            </Button>
          )}
        </Box>

        <CommentsWrap question={question} />
      </Box>
    </>
  );
}
